import { BrandTraxListing } from '../types/brandtraxListing';

export interface CatalogMsrp {
  value: number;
  currency: string;
}

const MSRP_PRICE_MULTIPLE = 100;
const MAX_SELLER_LENGTH = 200;
const MIN_TITLE_LENGTH = 2;
const MAX_TITLE_LENGTH = 500;
const MAX_QUANTITY = 1_000_000_000;
const MAX_ITEM_NUMBER_LENGTH = 64;
const MAX_DESCRIPTION_LENGTH = 20_000;

function normalizedText(value: string): string {
  return value.replace(/\s+/g, ' ').trim().toLowerCase();
}

function listingHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./i, '').toLowerCase();
  } catch {
    return '';
  }
}

function hostLabel(value: string): string {
  return value
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/.*$/, '')
    .toLowerCase();
}

function isHttpUrl(value: string): boolean {
  try {
    const protocol = new URL(value).protocol;
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

function currenciesMatch(listingCurrency: string | undefined, msrpCurrency: string): boolean {
  const left = String(listingCurrency || '')
    .trim()
    .toUpperCase();
  const right = String(msrpCurrency || '')
    .trim()
    .toUpperCase();
  return left.length > 0 && left === right;
}

/**
 * Reasonableness checks for a listing about to be sent to BrandTrax.
 * Returns an error string, or null when the listing may be injected.
 * Missing optional fields are allowed. A price below MSRP is allowed.
 */
export function validateListingForInject(
  listing: BrandTraxListing,
  msrp?: CatalogMsrp | null
): string | null {
  const reasons: string[] = [];

  if (listing.price != null) {
    const price = listing.price;
    if (!Number.isFinite(price) || price <= 0) {
      reasons.push('Listing price must be a positive number.');
    } else if (msrp && currenciesMatch(listing.listedCurrency, msrp.currency)) {
      const msrpValue = Number(msrp.value);
      if (Number.isFinite(msrpValue) && msrpValue > 0 && price > msrpValue * MSRP_PRICE_MULTIPLE) {
        const currency = String(listing.listedCurrency || msrp.currency)
          .trim()
          .toUpperCase();
        reasons.push(
          `Listing price ${price} ${currency} is far above MSRP ${msrpValue} ${currency}.`
        );
      }
    }
  }

  const seller = String(listing.seller || '').trim();
  const title = String(listing.title || '').trim();
  if (seller && title && normalizedText(seller) === normalizedText(title)) {
    reasons.push('Seller matches the product title.');
  }
  const host = listingHost(listing.url || '');
  if (seller && host && hostLabel(seller) === host) {
    reasons.push(`Seller "${seller}" is the marketplace host.`);
  }
  if (seller.length > MAX_SELLER_LENGTH) {
    reasons.push(`Seller is longer than ${MAX_SELLER_LENGTH} characters.`);
  }

  if (title.length < MIN_TITLE_LENGTH || title.length > MAX_TITLE_LENGTH) {
    reasons.push(`Title must be ${MIN_TITLE_LENGTH}–${MAX_TITLE_LENGTH} characters.`);
  }

  if (!isHttpUrl(String(listing.url || '').trim())) {
    reasons.push('Listing URL must be http or https.');
  }

  const sellerStoreUrl = String(listing.sellerStoreUrl || '').trim();
  if (sellerStoreUrl && !isHttpUrl(sellerStoreUrl)) {
    reasons.push('Seller store URL must be http or https.');
  }

  if (listing.stock != null && !plausibleQuantity(listing.stock)) {
    reasons.push(`Stock ${listing.stock} is not a plausible quantity.`);
  }
  if (listing.numberSold != null && !plausibleQuantity(listing.numberSold)) {
    reasons.push(`Units sold ${listing.numberSold} is not a plausible quantity.`);
  }

  const itemNumber = String(listing.itemNumber || '');
  if (itemNumber.trim()) {
    if (itemNumber.length > MAX_ITEM_NUMBER_LENGTH || /\s/.test(itemNumber)) {
      reasons.push('Item number is not a plausible identifier.');
    }
  }

  if (String(listing.body || '').length > MAX_DESCRIPTION_LENGTH) {
    reasons.push(`Description is longer than ${MAX_DESCRIPTION_LENGTH} characters.`);
  }

  if (reasons.length === 0) return null;
  return `Cannot send to BrandTrax. ${reasons.join(' ')}`;
}

function plausibleQuantity(value: number): boolean {
  return Number.isFinite(value) && value >= 0 && value <= MAX_QUANTITY;
}
